import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usersAPI, teamsAPI, tasksAPI, projectsAPI, timeLogsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, Badge, Button, Loading, Alert, Avatar } from '../components/ui';
import { ArrowLeft, UsersRound, Mail, Phone, Calendar, Briefcase, FolderKanban, CheckSquare, Clock, ChevronRight, Star, StarHalf } from 'lucide-react';
import './UserDetails.css';

const ROLE_COLORS = {
  Admin: '#c084fc',
  Manager: '#06d6a0',
  Member: '#60a5fa',
  Client: '#ffd166',
};

const projectMemberIds = (project) => {
  if (Array.isArray(project.members)) return project.members.map(Number);
  try { return JSON.parse(project.members || '[]').map(Number); } catch { return []; }
};

const userInProject = (project, userId) => {
  const id = Number(userId);
  return Number(project.project_lead_id) === id
    || Number(project.manager_id) === id
    || Number(project.client_id) === id
    || projectMemberIds(project).includes(id);
};

const getUserProjectRole = (project, userId) => {
  const id = Number(userId);
  if (Number(project.project_lead_id) === id) return 'Project Lead';
  if (Number(project.manager_id) === id) return 'Manager';
  if (Number(project.client_id) === id) return 'Client';
  if (projectMemberIds(project).includes(id)) return 'Member';
  return 'Contributor';
};

export default function UserDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [userTasks, setUserTasks] = useState([]);
  const [userProjects, setUserProjects] = useState([]);
  const [userTimeLogs, setUserTimeLogs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [userRes, tasksRes, projectsRes, logsRes, teamsRes] = await Promise.allSettled([
          usersAPI.get(id),
          tasksAPI.list({ assigned_to: id }),
          projectsAPI.list(),
          timeLogsAPI.list({ user_id: id }),
          teamsAPI.list(),
        ]);

        if (userRes.status === 'fulfilled') {
          setSelectedUser(userRes.value.data.data);
        } else {
          setError(userRes.reason?.response?.data?.message || 'User not found.');
          setLoading(false);
          return;
        }

        if (tasksRes.status === 'fulfilled') {
          setUserTasks(tasksRes.value.data.data || []);
        }
        if (projectsRes.status === 'fulfilled') {
          const allProjects = projectsRes.value.data.data || [];
          setUserProjects(allProjects.filter(p => userInProject(p, id)));
        }
        if (logsRes.status === 'fulfilled') {
          setUserTimeLogs(logsRes.value.data.data || []);
        }
        if (teamsRes.status === 'fulfilled') {
          setTeams(teamsRes.value.data.data || []);
        }
      } catch (err) {
        setError('An unexpected error occurred.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const userTeams = useMemo(() => {
    if (!selectedUser) return [];
    return teams.filter(t =>
      t.team_lead_id === selectedUser.user_id
      || t.members?.some(m => m.user_id === selectedUser.user_id)
    );
  }, [teams, selectedUser]);

  const tasksByProject = useMemo(() => {
    const groups = {};
    userTasks.forEach(task => {
      const key = task.project_id || 'none';
      if (!groups[key]) {
        groups[key] = {
          projectId: task.project_id,
          projectName: task.project?.project_name || 'Unassigned',
          tasks: [],
        };
      }
      groups[key].tasks.push(task);
    });
    return Object.values(groups);
  }, [userTasks]);

  const totalHoursLogged = useMemo(
    () => userTimeLogs.reduce((sum, log) => sum + (Number(log.hours_spent) || 0), 0),
    [userTimeLogs]
  );

  const goToProject = (projectId) => {
    navigate(`/app/projects?project_id=${projectId}`);
  };

  const goToTask = (taskId) => {
    navigate(`/app/tasks?task_id=${taskId}`);
  };

  if (loading) {
    return (
      <div className="user-details-page animate-in">
        <Loading message="Loading profile..." />
      </div>
    );
  }

  if (error || !selectedUser) {
    return (
      <div className="user-details-page animate-in">
        <Button variant="ghost" onClick={() => navigate(-1)} style={{ marginBottom: 20 }}>
          <ArrowLeft size={16} /> Back
        </Button>
        <Alert type="error" message={error || 'User not found'} />
      </div>
    );
  }
  
  const perfRating = selectedUser.rating || 0;
  const perfLabel = selectedUser.performanceLabel || 'Unrated';
  const fullStars = Math.floor(perfRating);
  const hasHalfStar = perfRating % 1 >= 0.5;
  const emptyStars = Math.max(0, 5 - fullStars - (hasHalfStar ? 1 : 0));

  return (
    <div className="user-details-page animate-in">
      <div className="user-details__back">
        <Button variant="ghost" onClick={() => navigate(-1)}>
          <ArrowLeft size={16} /> Back
        </Button>
      </div>

      <div className="user-details__header-card">
        <div className="user-details__profile-img">
          <Avatar
            name={selectedUser.name}
            size="xl"
            color={ROLE_COLORS[selectedUser.role] || 'var(--accent)'}
            style={{ borderRadius: '50%', width: 80, height: 80, fontSize: 28 }}
          />
        </div>
        <div className="user-details__header-info">
          <h1 className="user-details__title">{selectedUser.name}</h1>
          <div className="user-details__badges">
            <Badge value={selectedUser.role} />
            {selectedUser.user_id === me?.user_id && <span className="user-details__you-badge">You</span>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '12px' }}>
            <div style={{ display: 'flex', color: 'var(--accent)', alignItems: 'center' }}>
              {[...Array(fullStars)].map((_, i) => <Star key={`full-${i}`} size={16} fill="currentColor" />)}
              {hasHalfStar && <StarHalf size={16} fill="currentColor" />}
              {[...Array(emptyStars)].map((_, i) => <Star key={`empty-${i}`} size={16} />)}
              <span style={{ marginLeft: 6, fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{perfRating.toFixed(1)}</span>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--bg-card)', padding: '2px 8px', borderRadius: 999, border: '1px solid var(--border-bright)' }}>
              {perfLabel}
            </span>
          </div>
        </div>
      </div>

      <div className="user-details__content">
        <div className="user-details__main">
          {/* Profile Section */}
          <Card className="user-details__section-card">
            <h3 className="user-details__section-title">Profile Information</h3>
            <div className="user-details__info-grid">
              <div className="user-details__info-item">
                <Mail size={16} />
                <div>
                  <label>Email</label>
                  <span>{selectedUser.email || '—'}</span>
                </div>
              </div>
              <div className="user-details__info-item">
                <Phone size={16} />
                <div>
                  <label>Phone</label>
                  <span>{selectedUser.phone || '—'}</span>
                </div>
              </div>
              <div className="user-details__info-item">
                <Briefcase size={16} />
                <div>
                  <label>Designation</label>
                  <span>{selectedUser.title || 'Not set'}</span>
                </div>
              </div>
              <div className="user-details__info-item">
                <Calendar size={16} />
                <div>
                  <label>Joined</label>
                  <span>
                    {selectedUser.createdAt
                      ? new Date(selectedUser.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                      : '—'}
                  </span>
                </div>
              </div>
              {selectedUser.gender && (
                <div className="user-details__info-item">
                  <UsersRound size={16} />
                  <div>
                    <label>Gender</label>
                    <span>{selectedUser.gender}</span>
                  </div>
                </div>
              )}
              {selectedUser.unique_code && (
                <div className="user-details__info-item">
                  <span className="user-details__info-icon">#</span>
                  <div>
                    <label>Code</label>
                    <span>{selectedUser.unique_code}</span>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Assigned Tasks */}
          <Card className="user-details__section-card">
            <h3 className="user-details__section-title">Assigned Tasks ({userTasks.length})</h3>
            {userTasks.length === 0 ? (
              <p className="user-details__empty">No tasks assigned to this member.</p>
            ) : (
              <div className="user-details__task-groups">
                {tasksByProject.map(group => (
                  <div key={group.projectId || 'none'} className="user-details__task-group">
                    <div className="user-details__task-group-head">
                      {group.projectId ? (
                        <button
                          type="button"
                          className="user-details__task-group-project"
                          onClick={() => goToProject(group.projectId)}
                        >
                          <FolderKanban size={14} />
                          {group.projectName}
                        </button>
                      ) : (
                        <span className="user-details__task-group-project">{group.projectName}</span>
                      )}
                      <span className="user-details__task-group-count">{group.tasks.length} task{group.tasks.length !== 1 ? 's' : ''}</span>
                    </div>
                    <div className="user-details__list">
                      {group.tasks.map(task => (
                        <button
                          key={task.task_id}
                          type="button"
                          className="user-details__link-card user-details__link-card--task"
                          onClick={() => goToTask(task.task_id)}
                        >
                          <div className="user-details__link-card-icon user-details__link-card-icon--task">
                            <CheckSquare size={16} />
                          </div>
                          <div className="user-details__link-card-content">
                            <span className="user-details__link-card-title">{task.task_name}</span>
                            <span className="user-details__link-card-meta">
                              <Badge value={task.status} /> · <Badge value={task.priority} />
                            </span>
                          </div>
                          <ChevronRight size={16} className="user-details__link-card-arrow" />
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>

        <div className="user-details__side">
          {/* Stats */}
          <Card className="user-details__section-card">
            <h3 className="user-details__section-title">Activity Stats</h3>
            <div className="user-details__stats">
              <div className="user-details__stat">
                <FolderKanban size={18} />
                <span className="user-details__stat-value">{userProjects.length}</span>
                <span className="user-details__stat-label">Projects</span>
              </div>
              <div className="user-details__stat">
                <CheckSquare size={18} />
                <span className="user-details__stat-value">{userTasks.length}</span>
                <span className="user-details__stat-label">Tasks</span>
              </div>
              <div className="user-details__stat">
                <Clock size={18} />
                <span className="user-details__stat-value">{totalHoursLogged.toFixed(1)}h</span>
                <span className="user-details__stat-label">Logged</span>
              </div>
            </div>
          </Card>

          {/* Teams */}
          {userTeams.length > 0 && (
            <Card className="user-details__section-card">
              <h3 className="user-details__section-title">Teams ({userTeams.length})</h3>
              <div className="user-details__teams">
                {userTeams.map(t => (
                  <div
                    key={t.team_id}
                    className="user-details__team-chip"
                    onClick={() => navigate(`/app/teams/${t.team_id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <UsersRound size={14} />
                    <span>{t.name}</span>
                    {t.team_lead_id === selectedUser.user_id && (
                      <span className="user-details__team-chip-lead">Lead</span>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Projects */}
          <Card className="user-details__section-card">
            <h3 className="user-details__section-title">Projects ({userProjects.length})</h3>
            {userProjects.length === 0 ? (
              <p className="user-details__empty">Not assigned to any projects yet.</p>
            ) : (
              <div className="user-details__list">
                {userProjects.map(p => (
                  <button
                    key={p.project_id}
                    type="button"
                    className="user-details__link-card"
                    onClick={() => goToProject(p.project_id)}
                  >
                    <div className="user-details__link-card-icon" style={{ background: `${p.project_color || 'var(--accent)'}22`, color: p.project_color || 'var(--accent)' }}>
                      <FolderKanban size={16} />
                    </div>
                    <div className="user-details__link-card-content">
                      <span className="user-details__link-card-title">{p.project_name}</span>
                      <span className="user-details__link-card-meta">
                        {getUserProjectRole(p, selectedUser.user_id)} · <Badge value={p.status} />
                      </span>
                    </div>
                    <ChevronRight size={16} className="user-details__link-card-arrow" />
                  </button>
                ))}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
